/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType, useDevice } from '@squiz/dxp-console-support';
import { Alert, ButtonList, ButtonListProps } from '@squiz/sds';
import { ADMIN_CAPABILITY_STRINGS } from 'apps/administration/src/Constants/DxpCapabilityStrings.constants';
import clsx from 'clsx';
import { useConsoleServiceStore, useDxpFrameStore } from 'dxpConsoleCore-mf/State';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { DXP_ID_MAIN_CONTENT } from '../../Constants/ContentIds';
import { DxpCapability } from '../../Constants/DxpCapabilities/defs/DxpCapability.defs';
import { EntityDxpUserMeOrganisation } from '../../Services/console';
import { DxpScreenProps, dxpScreenDefaults } from './DxpScreen.props';
import { DXP_SCREEN_LAYOUT_BOX, DXP_SCREEN_LAYOUT_WIDE } from './types/DxpScreenLayout.types';
import {
  DXP_SCREEN_STRUCTURE_FULL,
  DXP_SCREEN_STRUCTURE_STANDARD,
  DXP_SCREEN_STRUCTURE_WIDE,
} from './types/DxpScreenStructure.types';

/**
 * DXP Console Screen.
 */
export const DxpScreen = (props: DxpScreenProps): JSX.Element => {
  const {
    buttons,
    alertProps,
    breadcrumbs,
    children,
    classes,
    heading,
    headingIcon,
    hideHeading,
    layout,
    structure,
    showSeparator,
    subHeading,
    title,
    description,
  } = {
    ...dxpScreenDefaults,
    ...props,
  };

  const { tenant }: { tenant: EntityDxpUserMeOrganisation } = useConsoleServiceStore();
  const { capability }: { capability: DxpCapability | null } = useDxpFrameStore();
  const { updateShowHeader, updateShowSideNavigation } = useDxpFrameStore();
  const device = useDevice();
  const [isLoading, setLoading] = useState(true);

  const buttonKeys = buttons?.map(() => uniqueId('button_')) ?? [];
  const buttonListProps: ButtonListProps = {
    buttonKeys,
    buttons: buttons ?? [],
    isWide: device === DeviceType.tabletSmall || device === DeviceType.mobile,
  };

  const mainHeaderClass = clsx(`main__header`, {
    [`main__header--breadcrumbs`]: breadcrumbs,
    [`main__header--detailed`]: subHeading,
    [`main__header--separator`]: showSeparator,
  });

  // Set page layout
  useEffect(() => {
    switch (structure) {
      case DXP_SCREEN_STRUCTURE_FULL:
        updateShowHeader(false);
        updateShowSideNavigation(false);
        break;
      case DXP_SCREEN_STRUCTURE_WIDE:
        updateShowHeader(true);
        updateShowSideNavigation(false);
        break;
      case DXP_SCREEN_STRUCTURE_STANDARD:
      default:
        updateShowHeader(true);
        updateShowSideNavigation(true);
        break;
    }

    // Screen is now ready to render
    setLoading(false);
  }, [isLoading, setLoading, structure, updateShowHeader, updateShowSideNavigation]);

  // Set HTML document title in the following formats:
  //   Top level:      Agencies and organizations - Squiz DXP
  //   Agency:         Title - Tenant - Squiz DXP
  //   Org Home:       Home - Tenant - Squiz DXP
  //   Administration: Title - Tenant - Squiz DXP
  //   General:        Title - Capability - Tenant - Squiz DXP
  useEffect(() => {
    const titleElements = [];

    // Fallback to heading value if title is undefined
    // However, title set to an empty string is valid
    // (This will remove it from the document title string)
    const pageTitle = title ?? heading;

    if (pageTitle) {
      titleElements.push(pageTitle);
    }

    if (capability && capability.title !== `Squiz DXP` && capability.title !== ADMIN_CAPABILITY_STRINGS.title) {
      titleElements.push(capability.title);
    }

    if (tenant) {
      titleElements.push(tenant.tenantName);
    }

    titleElements.push(`Squiz DXP`);

    document.title = titleElements.join(` - `);
  }, [capability, heading, tenant, title]);

  if (!isLoading) {
    if (layout === DXP_SCREEN_LAYOUT_WIDE) {
      return <>{children}</>;
    }

    return (
      <main
        className={clsx('main', classes, {
          [`has-buttons buttons-count-${buttons?.length}`]: buttons?.length,
        })}
        id={DXP_ID_MAIN_CONTENT}
        tabIndex={-1}
      >
        {layout === DXP_SCREEN_LAYOUT_BOX ? (
          <div className="box">
            <h1 className="sr-only">
              {headingIcon}
              {heading}
            </h1>
            {children}
          </div>
        ) : (
          <>
            <div className={mainHeaderClass}>
              {breadcrumbs}
              {alertProps && (
                <div className="main__header-alert">
                  <Alert {...alertProps} />
                </div>
              )}
              {hideHeading ? (
                <div className="main__header--title sr-only">
                  <h1 className="sr-only">
                    {headingIcon}
                    {heading}
                  </h1>
                  {description && <span className="sr-only">{description}</span>}
                </div>
              ) : (
                <div className="main__header-content">
                  <div className="main__header--title">
                    <h1>
                      {headingIcon}
                      {heading}
                    </h1>

                    {buttons && buttons.length > 0 && (
                      <div className="main__header-buttons">
                        <ButtonList {...buttonListProps} />
                      </div>
                    )}
                  </div>
                  {description && <span className="main__header-description">{description}</span>}
                </div>
              )}
              {subHeading && <h2 className="main__subheading">{subHeading}</h2>}
            </div>
            <div className="main__body">{children}</div>
          </>
        )}
      </main>
    );
  }

  return <></>;
};
